import { ContentLoading } from '@/components/common/content-loading';
import { ChangeRegionButton } from '../change-region-button';
import { FloorGuideHorizontalPoiModal, MinimalPoiModal } from '../floor-guide-poi-modal';
import { HorizontalCategoryList, HorizontalFloorList, HorizontalMapHeader } from '../horizontal';
import { NavigationGuide } from '../navigation-guide';
import { NearbyPoiListDropdown } from '../nearby-poi-list-dropdown';
import { NoCategoryPoiPopup } from '../no-category-poi-popup';
import { PoiListPopup } from '../poi-list';
import { SelectTransportationModeModal } from '../select-transportation-mode-modal';
import { ZoomControlBtnGroup } from '../zoom-control-btn-group';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '@/components/icons';
import { FOCUS_TO_MAX_ZOOM, FOCUS_TO_OBJECT_PADDING, MAP_CONTRAST_COLOR, OBJECT_GROUP, POI_ID_DELIMITER } from '@/constants/floor-guide';
import { zoomLevelToScale } from '@/constants/zoom';
import { BuildingMenuType } from '@/enums/building-menu';
import { FloorGuideType, NavigationStatus } from '@/enums/floor-guide';
import { useAccessibilityTTS, useRestoreFocusOnClose } from '@/hooks';
import { useCalculateIconSize, useExternalCategoryNavigation, useFloorGuideState, useMagnifyMapScrollThrough, usePoiContentModalState, usePoiContentState } from '@/hooks/floor-guide';
import { useBuildingCategory, useFloor, useMapControl, useMarker, useMyCurrentLocation, usePoi, useRouteDirection, useRouteSimulation } from '@/hooks/map';
import { useFloorCategory } from '@/hooks/map/useFloorCategory';
import { useMap } from '@/hooks/map/useMap';
import { useMapEvents } from '@/hooks/map/useMapEvents';
import { useMapMove } from '@/hooks/map/useMapMove';
import { useMapZoomLevel } from '@/hooks/map/useMapZoomLevel';
import { useCurrentBuilding } from '@/hooks/useCurrentBuilding';
import { useGlobalCountdownReset } from '@/hooks/useGlobalCountdownReset';
import { useInteraction } from '@/hooks/useInteraction';
import { useConfigStore, useModalStackStore } from '@/stores';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import { useMarkerStore } from '@/stores/markerStore';
import type { NearbyPoiItemType } from '@/types/map';
import type { SelectCategoryOptions } from '@/types/floor-guide';
import {
    generateFloorName,
    getMergedCategoryCodes,
    getNearestAvailablePoiItem,
    generateMapLanguage,
    getPoisByFloorAndCategory,
    isNonEmptyObject,
    resetObjectStyle,
    updateObjectGroup,
    updateObjectStyle,
} from '@/utils/floor-guide';
import { isMinimalPoiSlide } from '@/utils/floor-guide/isMinimalPoiSlide';
import { changeFloorAndWaitForEvent, findNearestFloorWithPoiIncludingAdjacentBuildings, generateQrHereUrl, getGroupedPoiForBuilding, getPoiObjectIds, safeMoveToMarker, waitForMarkerReady } from '@/utils/map';
import {
    addDistanceToPoiList,
    calculateEndPointFromPoi,
    calculateGetCurrentPoiOptions,
    getCurrentPois,
    getPoiById,
    sortNearbyPoiListByDistance,
    waitForPoisToLoad,
} from '@/utils/map-poi';
import { mapPoiListToMarker, mapPoiToMarker } from '@/utils/marker';
import { isValidNumber } from '@/utils/number';
import { buildNavigationParams, trackFloorGuideNavigation } from '@/utils/navigation-tracking';
import { generateDisplayDirections, generateGetRouteOptions } from '@/utils/route-simulation';
import clsx from 'clsx';
import { FOCUS_TYPE, LANG_TYPE, type TRANSPORTATION_REQUEST_TYPE } from 'dabeeomaps';
import type { IClosestPoiDataOption } from 'dabeeomaps/dist/src/model/IDataOption';
import type { INaviAnimOption } from 'dabeeomaps/dist/src/model/map/INaviAnimOption';
import type { IPoi } from 'dabeeomaps/dist/src/model/map/IPoi';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { TrackingService } from '@/services/analytics/TrackingService';
import type { FloorInfo } from '@/services/dto';
import type { KioskSelectedCategory } from '@/services/dto/kiosk-config';

export default function HorizontalView() {
    const { t } = useTranslation();
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const didMountRef = useRef(false);
    const processedPoiRef = useRef<string | null>(null);
    const pendingMarkerIdsRef = useRef<string[]>([]);
    const lastMarkerTimestampRef = useRef<number>(0);
    const ignoreNavigationCompleteRef = useRef(false);
    const currentMapFloorIdRef = useRef<string | undefined>(undefined);
    const shouldAbortNavigationComplete = (): boolean => {
        if (ignoreNavigationCompleteRef.current) {
            ignoreNavigationCompleteRef.current = false;
            return true;
        }
        return false;
    };

    const { speak } = useAccessibilityTTS();

    const { selectedPoi, floorGuideType, selectedCategoryNavigation, resetSelectedPoi, resetFloorGuideType, resetSelectedParkingSlot } = useFloorGuideStore();
    const {
        state,
        modalState,
        nearbyState,
        handleSetModalState,
        handleSetState,
        handleSetNearbyState,
        handleGetBuildingNameById,
        handleResetNearbyState,
        handleGetPoiContentById,
    } = useFloorGuideState();

    const server = useConfigStore((state) => state.config?.server);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const language = useConfigStore((state) => state.config.ui.language);
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const zoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);
    const zoomLevel = useConfigStore((state) => state.config.ui.zoomLevel);
    const barrierFreeScale = zoomLevelToScale(zoomLevel);
    const mapBarrierFreeStyle = useMemo((): CSSProperties => {
        if (zoomClosed || barrierFreeScale <= 1) {
            return {
                pointerEvents: 'auto',
                touchAction: 'auto',
            };
        }

        return {
            pointerEvents: 'none',
            transform: `scale(${1 / barrierFreeScale})`,
            transformOrigin: 'top left',
            width: `${barrierFreeScale * 100}%`,
            height: `${barrierFreeScale * 100}%`,
        };
    }, [zoomClosed, barrierFreeScale]);
    const buildings = useConfigStore((state) => state.config?.server?.store?.buildings);
    const categoryEnabled = useConfigStore(
        (state) => state.config?.server?.buildingMenus?.[state.config.ui.language]?.find((m) => m.menuType === BuildingMenuType.FLOOR)?.categoryEnabled ?? true
    );
    const { markerIds, setMarkerIds } = useMarkerStore();
    const { resetCountdown } = useGlobalCountdownReset();

    const { restoreFocus } = useRestoreFocusOnClose({ active: modalState.noPoiCategory });

    const { initMap, cleanupMap, mapData, currentMap, isMapReady, loading } = useMap({
        clientId: server?.store?.mapClientId ?? '',
        clientSecret: server?.store?.mapSecret ?? '',
        studioVersion: 4,
    });

    const cleanupMapRef = useRef(cleanupMap);

    // Update the ref whenever cleanupMap changes
    cleanupMapRef.current = cleanupMap;

    const {
        modalState: poiDetailModalState,
        handleSetModalState: handleSetPoiDetailModalState,
        startPoint,
        endPoint,
        setEndPointFromPoi,
        navigationOptions,
        walkingOptions,
        routes,
        selectedRoute,
        selectedEndPointId,
        poiItemBySelectedEndPointId,
        generateNavigationGuideEndPoint,
        currentPoiOptions,
        destinationIconUrl,
        isDestinationIconReady,
        setRoutes,
        setSelectedRoute,
        handlePoiClick,
        clickedMapPoiName,
        handleResetModalState,
        selectedContentId,
        setSelectedContentId,
    } = usePoiContentState(mapData, currentMap);

    const { currentBuilding } = useCurrentBuilding();

    const [currentMapBuildingId, setCurrentMapBuildingId] = useState<string | undefined>(
        () => currentBuilding?.studioBuildingId
    );

    const activeBuildingId = useMemo(
        () => currentMapBuildingId || currentBuilding?.studioBuildingId || '',
        [currentMapBuildingId, currentBuilding?.studioBuildingId]
    );

    const {
        selectedFloorName,
        handleChangeFloorByName,
        selectedFloor: selectedFloorItem,
        getFloorById,
        setSelectedFloorName,
        getFloorsByBuildingId,
        handleChangeFloor,
    } = useFloor(currentMap, mapData, activeBuildingId);
    const [sortedFloors, setSortedFloors] = useState<FloorInfo[]>([]);
    const { handleZoomIn, handleZoomOut, isDisabledZoomIn, isDisabledZoomOut, setZoomLevel } = useMapZoomLevel(currentMap);
    const { selectedCategory, setSelectedCategoryCode } = useBuildingCategory();
    const { addMarker, clearMarker } = useMarker(currentMap);
    const { groupedPoiByBuildingId } = usePoi(currentMap, mapData);
    const { poiContentSlide, relatedPoiContentList, selectedPoiContentId, setSelectedPoiContent } = usePoiContentModalState(
        selectedEndPointId,
        selectedFloorItem,
        clickedMapPoiName,
        selectedContentId
    );

    const isMinimalPoiDetail = isMinimalPoiSlide(poiContentSlide, language);

    const { calculateIconSize } = useCalculateIconSize();
    const { handleFocusTo, handleMoveTo } = useMapControl(currentMap);

    const { addMyCurrentLocation, clearMyCurrentLocation, updateMyLocationMarkerForZoom } = useMyCurrentLocation(currentMap);
    const { setPoi, resetPoi } = usePoi(currentMap);
    const { getRouteDirection } = useRouteDirection(mapData);
    const { drawRouteSimulation, startRouteSimulation, pauseRouteSimulation, stopRouteSimulation, resumeRouteSimulation, clearRouteSimulation } =
        useRouteSimulation(currentMap);
    const { moveUp, moveDown, moveLeft, moveRight } = useMapMove(currentMap);

    const allPoiItems = useMemo(() => {
        if (!buildings) {
            return [];
        }
        return buildings.flatMap((item) => (Array.isArray(item?.poiContents) ? item?.poiContents : []));
    }, [buildings]);

    const { selectedFloor } = useFloorCategory(selectedFloorName, currentMapBuildingId);

    const groupedPoiForCurrentBuilding = useMemo(
        () => getGroupedPoiForBuilding(groupedPoiByBuildingId, activeBuildingId, buildings),
        [groupedPoiByBuildingId, activeBuildingId, buildings]
    );

    const buildingFloorGroups = useMemo(() => {
        if (!buildings?.length) {
            return [];
        }
        return buildings
            .map((building) => ({
                studioBuildingId: building.studioBuildingId,
                name: building.name,
                floors: getFloorsByBuildingId(building.studioBuildingId),
            }))
            .filter((group) => group.floors.length > 0);
    }, [buildings, getFloorsByBuildingId]);

    const isMultiBuilding = buildingFloorGroups.length > 1;

    const sidebarBuildingName = useMemo(() => {
        return (
            handleGetBuildingNameById(activeBuildingId) ||
            currentBuilding?.name?.[language] ||
            currentBuilding?.name?.ko ||
            ''
        );
    }, [activeBuildingId, currentBuilding, handleGetBuildingNameById, language]);

    useEffect(() => {
        setSortedFloors(getFloorsByBuildingId(currentMapBuildingId || currentBuilding?.studioBuildingId || ''));
    }, [currentMapBuildingId, getFloorsByBuildingId]);

    const getPoiItemByStudioPoiId = useCallback(
        (studioPoiId: string) => {
            if (!Array.isArray(allPoiItems) || !allPoiItems?.length) {
                return undefined;
            }

            return allPoiItems.find((item) => item?.studioPoiId === studioPoiId);
        },
        [allPoiItems]
    );

    const handleStopSimulation = async () => {
        ignoreNavigationCompleteRef.current = true;
        stopRouteSimulation();
        clearRouteSimulation();
        handleSetModalState({ navigationGuide: false });
        setSelectedCategoryCode(undefined);
        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE });
        resetSelectedPoi();
        resetFloorGuideType();
        resetSelectedParkingSlot();

        await handleChangeFloor(startPoint?.floorId ?? '');

        setCurrentMapBuildingId(currentBuilding?.studioBuildingId);

        await addMyCurrentLocation();
    };

    const applyCategoryOnFloor = async (
        targetFloor: FloorInfo,
        buildingCategory: KioskSelectedCategory,
        targetBuildingId?: string
    ): Promise<void> => {
        const selectedFloorName = targetFloor.name[language || 'ko'] || '';
        const selectedFloorNameKo = targetFloor.name['ko'] || '';
        if (!selectedFloorNameKo) {
            return;
        }

        const buildingId = targetBuildingId || targetFloor.buildingId || activeBuildingId;
        if (buildingId && buildingId !== activeBuildingId) {
            setCurrentMapBuildingId(buildingId);
        }

        await handleChangeFloor((targetFloor as FloorInfo & { floor_code?: string }).floor_code ?? targetFloor.floorCode);
        if (selectedFloorName) {
            setSelectedFloorName(selectedFloorName);
        }
        if (Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length) {
            clearMarker(markerIds?.poiList);
            setMarkerIds({ poiList: undefined });
        }

        const groupedPoiForTargetBuilding = getGroupedPoiForBuilding(groupedPoiByBuildingId, buildingId, buildings);
        const poiList = getPoisByFloorAndCategory(groupedPoiForTargetBuilding, selectedFloorNameKo, buildingCategory);

        if (!Array.isArray(poiList) || !poiList?.length) {
            return;
        }
        const nearbyPoiList: NearbyPoiItemType[] = [];
        for (const poi of poiList) {
            const closestPoiOptions: IClosestPoiDataOption = {
                floorId: poi?.floorId ?? '',
                position: {
                    x: poi?.position?.x ?? 0,
                    y: poi?.position?.y ?? 0,
                },
            };
            const orderedPoiList = getCurrentPois(mapData, closestPoiOptions);
            if (Array.isArray(orderedPoiList) && orderedPoiList?.length > 0) {
                nearbyPoiList.push({
                    ...poi,
                    nearbyPoi: getNearestAvailablePoiItem(orderedPoiList, allPoiItems),
                });
            }
        }
        if (nearbyPoiList?.length) {
            const poiListWithDistance = await addDistanceToPoiList(startPoint, nearbyPoiList, mapData);
            const sortedNearbyPoiList = sortNearbyPoiListByDistance(poiListWithDistance);
            const buildingName = handleGetBuildingNameById(buildingId || currentBuilding?.studioBuildingId || '');
            if (sortedNearbyPoiList?.length) {
                const selectedPoiId = `${sortedNearbyPoiList[0]?.id}${POI_ID_DELIMITER}${sortedNearbyPoiList[0]?.nearbyPoi?.id}`;
                handleSetNearbyState({
                    nearbyPoiList: sortedNearbyPoiList,
                    selectedPoiId: selectedPoiId,
                    title: buildingCategory?.name || '',
                    floorName: selectedFloorName ?? '',
                    building: buildingName ?? '',
                    type: 'category',
                });
                handleSetModalState({ nearbyPoiList: true });
            }
        }
        const markers = mapPoiListToMarker(poiList, calculateIconSize({ width: 35, height: 48 }), destinationIconUrl);
        const newMarkerIds = await addMarker({ marker: markers });
        setMarkerIds({ poiList: newMarkerIds });
        const objectIds = getPoiObjectIds(poiList);
        if (!objectIds?.length) {
            return;
        }
        if (objectIds.length >= 1) {
            setTimeout(() => {
                handleFocusTo({
                    transition: true,
                    maxZoom: FOCUS_TO_MAX_ZOOM,
                    focus: {
                        type: FOCUS_TYPE.OBJECT,
                        ids: objectIds,
                    },
                    padding: FOCUS_TO_OBJECT_PADDING.MULTI_OBJECT,
                });
            }, 100);
            return;
        }
        setTimeout(() => {
            handleMoveTo({
                position: {
                    x: poiList[0]?.position?.x ?? 0,
                    y: poiList[0]?.position?.y ?? 0,
                },
                floorId: poiList[0]?.floorId,
                transition: true,
            });
        }, 100);
    };

    const handleSelectCategory = async (buildingCategory: KioskSelectedCategory, options?: SelectCategoryOptions) => {
        const shouldSyncMapCategoryBar = options?.syncMapCategoryBar ?? true;

        // Create unique timestamp for this call
        const currentTimestamp = Date.now();
        lastMarkerTimestampRef.current = currentTimestamp;

        // clear route simulation
        handleSetModalState({ nearbyPoiList: false, navigationGuide: false });
        handleResetNearbyState();
        stopRouteSimulation();
        clearRouteSimulation();
        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE });
        resetSelectedPoi();
        resetFloorGuideType();

        // Clear pending markers from previous calls that haven't completed yet
        if (Array.isArray(pendingMarkerIdsRef.current) && pendingMarkerIdsRef.current?.length > 0) {
            clearMarker(pendingMarkerIdsRef.current);
            pendingMarkerIdsRef.current = [];
        }

        // Clear existing markers
        if (Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length) {
            clearMarker(markerIds?.poiList);
        }

        if (markerIds?.selectedPoi) {
            clearMarker(markerIds?.selectedPoi);
        }

        // Reset marker state immediately
        setMarkerIds({ poiList: undefined, selectedPoi: undefined });

        // Small delay to ensure markers are cleared before adding new ones
        await new Promise((resolve) => setTimeout(resolve, 100));
        // if (!buildingCategory?.clientCode) {
        //     return;
        // }
        const selectedFloorNameKo = selectedFloor?.name?.['ko'] || '';

        const categoryCodes = getMergedCategoryCodes(buildingCategory);
        const poiList = getPoisByFloorAndCategory(groupedPoiForCurrentBuilding, selectedFloorNameKo, buildingCategory);

        if (!poiList?.length || poiList?.length === 0) {
            const nearestFloorResult = findNearestFloorWithPoiIncludingAdjacentBuildings(
                sortedFloors,
                selectedFloorNameKo,
                groupedPoiForCurrentBuilding,
                categoryCodes,
                activeBuildingId,
                groupedPoiByBuildingId,
                buildings
            );
            const nearestFloor = nearestFloorResult?.floor ?? null;
            handleSetState({ nearestPoiFloor: nearestFloor });
            setMarkerIds({ poiList: undefined, selectedPoi: undefined });
            if (nearestFloor && nearestFloorResult) {
                if (shouldSyncMapCategoryBar) {
                    setSelectedCategoryCode(buildingCategory?.categoryCode);
                }
                if (options?.autoSwitchToNearestFloor) {
                    await applyCategoryOnFloor(nearestFloor, buildingCategory, nearestFloorResult.buildingId);
                    return;
                }
                handleSetModalState({ noPoiCategory: true });
                return;
            }
            return;
        }

        // NEARBY
        if (Array.isArray(poiList) && poiList?.length) {
            const nearbyPoiList: NearbyPoiItemType[] = [];
            for (const poi of poiList) {
                const closestPoiOptions: IClosestPoiDataOption = {
                    floorId: poi?.floorId ?? '',
                    position: {
                        x: poi?.position?.x ?? 0,
                        y: poi?.position?.y ?? 0,
                    },
                };

                const orderedPoiList = getCurrentPois(mapData, closestPoiOptions);
                nearbyPoiList.push({
                    ...poi,
                    nearbyPoi: getNearestAvailablePoiItem(orderedPoiList, allPoiItems),
                });
            }

            const poiListWithDistance = await addDistanceToPoiList(startPoint, nearbyPoiList, mapData);
            const sortedNearbyPoiList = sortNearbyPoiListByDistance(poiListWithDistance);

            const buildingName = handleGetBuildingNameById(currentMapBuildingId || currentBuilding?.studioBuildingId || '');

            if (sortedNearbyPoiList?.length) {
                const selectedPoiId = `${sortedNearbyPoiList[0]?.id}${POI_ID_DELIMITER}${sortedNearbyPoiList[0]?.nearbyPoi?.id}`;
                handleSetNearbyState({
                    nearbyPoiList: sortedNearbyPoiList,
                    selectedPoiId: selectedPoiId,
                    title: buildingCategory?.name || '',
                    floorName: selectedFloorName ?? '',
                    building: buildingName ?? '',
                    type: 'category',
                });
                handleSetModalState({ nearbyPoiList: true });
            }
        }

        const markers = mapPoiListToMarker(poiList, calculateIconSize({ width: 35, height: 48 }), destinationIconUrl);
        const newMarkerIds = await addMarker({ marker: markers });

        // Check again if this is still the latest call after async operation
        if (currentTimestamp !== lastMarkerTimestampRef.current) {
            // Newer call has been made, clean up markers we just added
            if (Array.isArray(newMarkerIds) && newMarkerIds?.length) {
                clearMarker(newMarkerIds);
            }
            return;
        }

        // Store marker IDs in ref for cleanup in next call
        if (Array.isArray(newMarkerIds) && newMarkerIds?.length) {
            pendingMarkerIdsRef.current = newMarkerIds;
        }

        if (markers?.length) {
            speak(t('floor_guide.text_to_speech.click.location_this_floor', { number: markers?.length }));
        }

        setMarkerIds({ poiList: newMarkerIds });
        if (shouldSyncMapCategoryBar) {
            setSelectedCategoryCode(buildingCategory?.categoryCode);
        }

        const objectIds = getPoiObjectIds(poiList);

        if (!objectIds?.length) {
            return;
        }

        if (objectIds.length >= 1) {
            handleFocusTo({
                transition: true,
                maxZoom: FOCUS_TO_MAX_ZOOM,
                focus: {
                    type: FOCUS_TYPE.OBJECT,
                    ids: objectIds,
                },
                padding: FOCUS_TO_OBJECT_PADDING.MULTI_OBJECT,
            });
            return;
        }

        handleMoveTo({
            position: {
                x: poiList[0]?.position?.x ?? 0,
                y: poiList[0]?.position?.y ?? 0,
            },
            floorId: poiList[0]?.floorId,
            transition: true,
        });
    };

    useExternalCategoryNavigation({
        handleSelectCategory,
        isMapReady,
        isDestinationIconReady,
    });

    const handleGoToFloor = async () => {
        if (!state.nearestPoiFloor || !selectedCategory) {
            return;
        }
        await applyCategoryOnFloor(
            state.nearestPoiFloor,
            selectedCategory,
            state.nearestPoiFloor.buildingId || activeBuildingId
        );
        handleSetModalState({ noPoiCategory: false });
    };

    const handleCloseNearbyPoiList = async () => {
        handleSetModalState({ nearbyPoiList: false });
        handleResetNearbyState();
        setSelectedCategoryCode(undefined);

        ignoreNavigationCompleteRef.current = true;
        stopRouteSimulation();
        clearRouteSimulation();

        handleSetModalState({ navigationGuide: false });

        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE, isShowAllFloorGuide: false });
        resetSelectedPoi();
        resetFloorGuideType();

        await handleChangeFloor(startPoint?.floorId ?? '');

        setCurrentMapBuildingId(currentBuilding?.studioBuildingId);
        await addMyCurrentLocation();
    };

    const handleStart = useCallback(
        (animationOptions?: INaviAnimOption) => {
            ignoreNavigationCompleteRef.current = false;
            startRouteSimulation(animationOptions);
            handleSetState({ navigationStatus: NavigationStatus.RUNNING });
        },
        [startRouteSimulation, handleSetState]
    );

    const handleStartNavigationGuide = async (poi: IPoi) => {
        // Set endpoint - no timing issues since it's direct state!
        setEndPointFromPoi(poi);

        // Calculate endpoint for immediate use
        const calculatedEndPoint = calculateEndPointFromPoi(poi, mapData);

        // Use immediately
        const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, calculatedEndPoint));
        if (!routeDirections || !Object.keys(routeDirections)?.length) {
            return;
        }

        clearMyCurrentLocation();

        setRoutes(routeDirections);

        if (startPoint?.floorName !== calculatedEndPoint.floorName) {
            handleSetModalState({ selectTransportationMode: true });
            handleResetModalState();
            return;
        }

        setSelectedRoute(routeDirections?.recommendation);
        await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
        trackFloorGuideNavigation(
            buildNavigationParams({
                startBuildingId: server?.building?.studioBuildingId ?? '',
                startPoint,
                endBuildingId: currentMapBuildingId || currentBuilding?.studioBuildingId || '',
                endPoint: calculatedEndPoint,
                endPoiId: poi?.id ?? '',
            })
        );
        handleResetModalState();
        handleSetModalState({ navigationGuide: true });
        // speak(t('floor_guide.text_to_speech.starting_route_guidance'));
        handleStart(walkingOptions);
    };

    const handleMinimalPoiLocationCheck = async () => {
        handleSetPoiDetailModalState({ poiDetail: false });
        if (state.shouldReopenPoiList) {
            handleSetState({ shouldReopenPoiList: false });
        }
        handleSetModalState({ poiList: false });

        const studioPoiId = poiContentSlide?.data?.studioPoiId || selectedEndPointId;
        if (!studioPoiId) return;

        const poiItem = getPoiById(mapData?.dataPoi?.getPois(), studioPoiId);
        if (!poiItem) return;

        const poiListMarkers = Array.isArray(markerIds?.poiList) ? markerIds.poiList : [];
        const selectedPoiMarkerId = markerIds?.selectedPoi ? [markerIds.selectedPoi] : [];
        clearMarker([...poiListMarkers, ...selectedPoiMarkerId]);
        setMarkerIds({ selectedPoi: undefined, poiList: undefined });

        const floor = mapData?.dataFloor?.getFloors()?.find((item) => item.id === poiItem.floorId);
        if (floor) {
            await handleChangeFloor(floor.id);
        }

        const poiMarker = mapPoiToMarker(poiItem, calculateIconSize({ width: 35, height: 48 }), destinationIconUrl);
        const newMarkerIds = await addMarker({ marker: poiMarker ? [poiMarker] : [] });

        if (Array.isArray(newMarkerIds) && newMarkerIds.length) {
            const markerId = newMarkerIds[0];
            setMarkerIds({ selectedPoi: markerId });

            if (isValidNumber(poiItem.position?.x) && isValidNumber(poiItem.position?.y)) {
                await waitForMarkerReady(currentMap, markerId, { x: poiItem.position.x, y: poiItem.position.y }, 10, 'MinimalPoi-LocationCheck');
                await safeMoveToMarker(currentMap, { x: poiItem.position.x, y: poiItem.position.y }, poiItem.floorId, true, 3, 'MinimalPoi-LocationCheck');
            }
        }

        speak(t('floor_guide.text_to_speech.click.location_this_floor', { number: 1 }));
    };

    const handleStartGuide = async () => {
        if (!endPoint) return;

        const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, endPoint));
        if (!routeDirections || !Object.keys(routeDirections)?.length) {
            return;
        }

        clearMyCurrentLocation();

        setRoutes(routeDirections);
        if (startPoint?.floorName !== endPoint?.floorName) {
            handleSetModalState({ selectTransportationMode: true, poiList: false });
            handleResetModalState();
            return;
        }
        setSelectedRoute(routeDirections?.recommendation);
        await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
        trackFloorGuideNavigation(
            buildNavigationParams({
                startBuildingId: server?.building?.studioBuildingId ?? '',
                startPoint,
                endBuildingId: currentMapBuildingId || currentBuilding?.studioBuildingId || '',
                endPoint,
                endPoiId: selectedEndPointId ?? endPoint.poiId ?? '',
            })
        );
        handleResetModalState();
        handleSetModalState({ navigationGuide: true, poiList: false });
        // speak(t('floor_guide.text_to_speech.starting_route_guidance'));
        handleStart(walkingOptions);
    };

    const handleSelectTransportationMode = async (mode: TRANSPORTATION_REQUEST_TYPE) => {
        if (!routes || !Object.keys(routes)?.length || !routes[mode]) {
            return;
        }
        if (endPoint) {
            trackFloorGuideNavigation(
                buildNavigationParams({
                    startBuildingId: server?.building?.studioBuildingId ?? '',
                    startPoint,
                    endBuildingId: currentMapBuildingId || currentBuilding?.studioBuildingId || '',
                    endPoint,
                    endPoiId: selectedEndPointId ?? endPoint.poiId ?? '',
                    transportMode: mode,
                })
            );
        }
        setSelectedRoute(routes[mode]);
        await drawRouteSimulation(routes[mode], navigationOptions);
        handleResetModalState();
        handleSetModalState({ navigationGuide: true, selectTransportationMode: false });
        // speak(t('floor_guide.text_to_speech.starting_route_guidance'));
        handleStart(walkingOptions);
    };

    const handleResumeSimulation = () => {
        resumeRouteSimulation();
        handleSetState({ navigationStatus: NavigationStatus.RUNNING });
    };

    const handlePauseSimulation = () => {
        pauseRouteSimulation();
        handleSetState({ navigationStatus: NavigationStatus.PAUSED });
    };

    const handleWatchAgain = () => {
        startRouteSimulation(walkingOptions);
        handleSetState({ navigationStatus: NavigationStatus.RUNNING });
    };

    const handleShowMyLocation = async (type?: 'show-location') => {
        handleSetModalState({ nearbyPoiList: false });
        handleResetNearbyState();
        ignoreNavigationCompleteRef.current = true;
        stopRouteSimulation();
        clearRouteSimulation();

        handleSetModalState({ navigationGuide: false });
        setSelectedCategoryCode(undefined);

        setRoutes(undefined);
        handleSetState({ navigationStatus: NavigationStatus.DONE, isShowAllFloorGuide: false });
        resetSelectedPoi();
        resetFloorGuideType();

        await handleChangeFloor(startPoint?.floorId ?? '');

        setCurrentMapBuildingId(currentBuilding?.studioBuildingId);

        if (type === 'show-location') {
            const currentPoiList = getCurrentPois(mapData, currentPoiOptions);
            if (Array.isArray(currentPoiList) && currentPoiList?.length && currentPoiList[0]?.id) {
                const poiItem = getPoiItemByStudioPoiId(currentPoiList[0]?.id);
                if (poiItem) {
                    speak(
                        `${t('floor_guide.text_to_speech.current_location', { floorName: generateFloorName(startPoint?.floorName ?? ''), poiName: poiItem?.name?.[language] || poiItem?.name?.['ko'] || '' })}`
                    );
                }
            }
        }
        await addMyCurrentLocation();
    };

    useMapEvents({
        mapContainer: mapContainerRef,
        onPoiClick: async (data) => {
            stopRouteSimulation();
            clearRouteSimulation();

            handleSetModalState({ navigationGuide: false, nearbyPoiList: false });

            setRoutes(undefined);
            handleSetState({ navigationStatus: NavigationStatus.DONE, isShowAllFloorGuide: false });
            resetSelectedPoi();
            resetFloorGuideType();
            handlePoiClick(data);
            handleResetNearbyState();
        },
        onNavigationComplete: async () => {
            if (shouldAbortNavigationComplete()) {
                return;
            }
            handleSetState({ navigationStatus: NavigationStatus.DONE });
            setRoutes(undefined);
            if (endPoint?.floorName) {
                setSelectedFloorName(endPoint.floorName);
            }
            if (endPoint && isValidNumber(endPoint.position?.x) && isValidNumber(endPoint.position?.y)) {
                const needsFloorChange = Boolean(endPoint.floorId && endPoint.floorId !== currentMapFloorIdRef.current);
                if (needsFloorChange && mapContainerRef.current && endPoint.floorId) {
                    await changeFloorAndWaitForEvent(currentMap, mapContainerRef.current, endPoint.floorId);
                    if (shouldAbortNavigationComplete()) {
                        return;
                    }
                }
                if (shouldAbortNavigationComplete()) {
                    return;
                }
                await safeMoveToMarker(
                    currentMap,
                    { x: endPoint.position.x, y: endPoint.position.y },
                    endPoint.floorId,
                    true,
                    3,
                    'NavComplete-Horizontal'
                );
            }
        },
        onFloorChanged: (data: { buildingId: string; name?: { lang: string; text: string }[]; floorId?: string; id?: string }) => {
            const nextFloorId = data?.floorId ?? data?.id;
            if (nextFloorId) {
                currentMapFloorIdRef.current = nextFloorId;
            }
            if (Array.isArray(data?.name) && data.name.length) {
                const nextFloorName =
                    data.name.find((item) => item.lang === language)?.text ||
                    data.name.find((item) => item.lang === 'ko')?.text ||
                    data.name[0]?.text ||
                    '';
                if (nextFloorName) {
                    setSelectedFloorName(nextFloorName);
                }
            }

            setCurrentMapBuildingId(data?.buildingId);

            if (contrast) {
                updateObjectGroup(mapData, OBJECT_GROUP);
                updateObjectStyle(currentMap, OBJECT_GROUP);
                setPoi(MAP_CONTRAST_COLOR.POI);
            } else {
                resetObjectStyle(currentMap);
                resetPoi();
            }
        },
        onZoomChanged: (data) => {
            const nextZoom = data?.changedValue ?? 1;
            setZoomLevel(nextZoom);
            void updateMyLocationMarkerForZoom(nextZoom);
        },
    });

    useEffect(() => {
        initMap(mapContainerRef);
        return () => {
            cleanupMapRef.current();
        };
    }, [initMap]);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        const container = mapContainerRef.current;
        if (!container) return;

        cleanupMap();

        let isCancelled = false;
        let debounceTimer: ReturnType<typeof setTimeout> | undefined;

        const tryInitMap = (): void => {
            if (isCancelled || !mapContainerRef.current) return;
            observer.disconnect();
            if (debounceTimer) clearTimeout(debounceTimer);
            clearTimeout(fallbackTimer);
            initMap(mapContainerRef);
        };

        const scheduleInit = (): void => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(tryInitMap, 100);
        };

        const observer = new ResizeObserver(scheduleInit);
        observer.observe(container);
        scheduleInit();
        const fallbackTimer = setTimeout(tryInitMap, 350);

        return () => {
            isCancelled = true;
            observer.disconnect();
            if (debounceTimer) clearTimeout(debounceTimer);
            clearTimeout(fallbackTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenMode]);

    useEffect(() => {
        if (contrast) {
            updateObjectGroup(mapData, OBJECT_GROUP);
            updateObjectStyle(currentMap, OBJECT_GROUP);
            setPoi(MAP_CONTRAST_COLOR.POI);
        } else {
            resetObjectStyle(currentMap);
            resetPoi();
        }
    }, [currentMap, mapData, contrast, resetPoi, setPoi]);

    useEffect(() => {
        return () => {
            cleanupMapRef.current();
        };
    }, []);

    useEffect(() => {
        const run = async () => {
            const isExternalCategoryNavigation =
                floorGuideType === FloorGuideType.CATEGORY || !!selectedCategoryNavigation;
            if (!selectedPoi && !isExternalCategoryNavigation) {
                currentMap?.context?.changeLanguage?.(generateMapLanguage(language) as LANG_TYPE);
                await handleShowMyLocation();
            }
        };
        run();
    }, [language]);

    useEffect(() => {
        const run = async () => {
            if (!selectedPoi?.studioFloorID || !selectedPoi?.studioPoiId || !floorGuideType || !isMapReady || !isDestinationIconReady) {
                return;
            }

            // Kiểm tra xem POI này đã được xử lý chưa để tránh chạy lại
            const currentPoiKey = `${selectedPoi.studioPoiId}-${floorGuideType}`;
            if (processedPoiRef.current === currentPoiKey) {
                return;
            }

            // Đánh dấu đang xử lý POI này
            processedPoiRef.current = currentPoiKey;

            const poiItem = getPoiById(mapData?.dataPoi?.getPois(), selectedPoi.studioPoiId);
            const floor = mapData?.dataFloor?.getFloors()?.find((item) => item.id === selectedPoi?.studioFloorID);
            setEndPointFromPoi(poiItem);
            if (selectedPoi?.contentId) {
                setSelectedContentId(selectedPoi.contentId);
            }

            if (selectedPoi && floorGuideType === FloorGuideType.MARKER) {
                const options = calculateGetCurrentPoiOptions(poiItem as IPoi);

                const currentPoiList = await waitForPoisToLoad(mapData, options, 'selectedPoi-MARKER');

                const nearbyPoiItem = getNearestAvailablePoiItem(currentPoiList, allPoiItems);

                const poiContent = handleGetPoiContentById(selectedPoi?.studioPoiId, selectedPoi?.contentId);

                const building = buildings?.find((b) => b.studioBuildingId === poiContent?.studioBuildingId);
                const buildingName = building?.name?.[language] || building?.name?.['ko'] || '';

                setCurrentMapBuildingId(selectedPoi?.buildingId ?? currentBuilding?.studioBuildingId);

                if (Array.isArray(currentPoiList) && currentPoiList?.length) {
                    const POINearByCurrentPoi = nearbyPoiItem;
                    const selectedPoiId = `${poiItem?.id}${POI_ID_DELIMITER}${POINearByCurrentPoi?.id}`;
                    handleSetNearbyState({
                        nearbyPoiList: [{ ...(poiItem as IPoi), nearbyPoi: POINearByCurrentPoi }],
                        selectedPoiId: selectedPoiId,
                        title: poiContent?.name?.[language] || poiContent?.name?.['ko'] || '',
                        floorName: floor?.name.find((item) => item.lang === language)?.text ?? '',
                        building: buildingName ?? '',
                        type: 'show-location',
                    });
                    handleSetModalState({ nearbyPoiList: true });
                }

                // if (!isJamsil) {
                //     await handleChangeFloorByName(floor?.name.find((item) => item.lang === language)?.text ?? '');
                // } else {
                await handleChangeFloor(floor?.id);
                const floorDisplayName = floor?.name?.find((item) => item.lang === language)?.text;
                // }

                if (isValidNumber(poiItem?.position?.x) && isValidNumber(poiItem?.position?.y)) {
                    const poiMarker = mapPoiToMarker(poiItem, calculateIconSize({ width: 35, height: 48 }), destinationIconUrl);
                    const poiPosition = { x: poiItem.position.x, y: poiItem.position.y };
                    const [, markerIds] = await Promise.all([
                        safeMoveToMarker(currentMap, poiPosition, poiItem?.floorId, true, 3, 'MARKER-FloorGuide-Horizontal'),
                        addMarker({ marker: poiMarker ? [poiMarker] : [] }),
                    ]);
                    if (Array.isArray(markerIds) && markerIds?.length) {
                        setMarkerIds({ selectedPoi: markerIds[0] });
                    }
                }
                // reset State
                // if (!isJamsil) {
                //     resetSelectedPoi();
                // }
                resetFloorGuideType();
                processedPoiRef.current = null;

                if (floorDisplayName) {
                    setSelectedFloorName(floorDisplayName);
                }
                return;
            }

            // create route
            const destinationPoint = {
                floorId: poiItem?.floorId ?? '',
                floorName: floor?.name[0].text ?? '',
                position: {
                    x: poiItem?.position?.x ?? 0,
                    y: poiItem?.position?.y ?? 0,
                },
                poiId: poiItem?.id,
            };

            const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, destinationPoint));
            if (!routeDirections || !Object.keys(routeDirections)?.length) {
                processedPoiRef.current = null;
                return;
            }

            clearMyCurrentLocation();
            setRoutes(routeDirections);
            if (startPoint?.floorName !== destinationPoint.floorName) {
                handleSetModalState({ selectTransportationMode: true });
                handleResetModalState();
                resetSelectedPoi();
                resetFloorGuideType();
                processedPoiRef.current = null;
                return;
            }

            setSelectedRoute(routeDirections?.recommendation);
            await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
            trackFloorGuideNavigation(
                buildNavigationParams({
                    startBuildingId: server?.building?.studioBuildingId ?? '',
                    startPoint,
                    endBuildingId: selectedPoi?.buildingId || currentMapBuildingId || currentBuilding?.studioBuildingId || '',
                    endPoint: destinationPoint,
                    endPoiId: poiItem?.id ?? '',
                })
            );
            handleResetModalState();
            handleSetModalState({ navigationGuide: true });
            handleStart(walkingOptions);
            handleSetState({ navigationStatus: NavigationStatus.RUNNING });

            // reset state
            resetSelectedPoi();
            resetFloorGuideType();
            processedPoiRef.current = null;
        };

        run();
    }, [
        currentMap,
        selectedPoi,
        floorGuideType,
        isMapReady,
        isDestinationIconReady,
        destinationIconUrl,
        startPoint,
        mapData?.dataPoi,
        mapData?.dataFloor,
        navigationOptions,
        walkingOptions,
        markerIds.myLocation,
        calculateIconSize,
        handleChangeFloorByName,
        getRouteDirection,
        handleSetModalState,
        handleResetModalState,
        setRoutes,
        resetSelectedPoi,
        resetFloorGuideType,
        addMarker,
        handleStart,
        drawRouteSimulation,
        setSelectedRoute,
        clearMarker,
        setMarkerIds,
        handleSetState,
        setEndPointFromPoi,
        handleSetNearbyState,
        currentMap?.pois?.getCurrentPois,
        handleGetPoiContentById,
        selectedFloorName,
        handleGetBuildingNameById,
        mapData,
        allPoiItems,
        setSelectedContentId,
    ]);

    const disableMouseEvent = useCallback(() => {
        currentMap?.control?.setOption({
            mouseOption: {
                enableZoom: false,
                enablePan: false,
            },
            touchOption: {
                enableZoom: false,
                enablePan: false,
            },
        });
    }, [currentMap]);

    const enableMouseEvent = useCallback(() => {
        currentMap?.control?.setOption({
            mouseOption: {
                enableZoom: true,
                enablePan: true,
            },
            touchOption: {
                enableZoom: true,
                enablePan: true,
            },
        });
    }, [currentMap]);

    const isPoiDetailModalOpen = poiDetailModalState.poiDetail;

    const mapContainerStyle = useMemo((): CSSProperties => {
        if (isPoiDetailModalOpen && !zoomClosed) {
            return {
                ...mapBarrierFreeStyle,
                pointerEvents: 'none',
            };
        }

        return mapBarrierFreeStyle;
    }, [isPoiDetailModalOpen, mapBarrierFreeStyle, zoomClosed]);

    const mapInnerStyle = useMemo((): CSSProperties => {
        if (!zoomClosed && isPoiDetailModalOpen) {
            return { pointerEvents: 'none' };
        }

        return { pointerEvents: 'auto' };
    }, [zoomClosed, isPoiDetailModalOpen]);

    useEffect(() => {
        if (!currentMap) {
            return;
        }

        if (zoomClosed) {
            enableMouseEvent();
        } else {
            disableMouseEvent();
        }
    }, [currentMap, enableMouseEvent, disableMouseEvent, zoomClosed]);

    useMagnifyMapScrollThrough(!zoomClosed, isMapReady);

    useEffect(() => {
        if (zoomClosed || !isPoiDetailModalOpen) {
            return;
        }

        const kioskLayout = containerRef.current?.closest('[class*="kiosk-layout--"]');
        const zoomPanContainer = kioskLayout?.parentElement ?? null;

        if (!zoomPanContainer) {
            return;
        }

        const { overflow, overflowY } = window.getComputedStyle(zoomPanContainer);
        if (overflow !== 'auto' && overflowY !== 'auto') {
            return;
        }

        const previousOverflow = zoomPanContainer.style.overflow;
        zoomPanContainer.style.overflow = 'hidden';

        return () => {
            zoomPanContainer.style.overflow = previousOverflow;
        };
    }, [zoomClosed, isPoiDetailModalOpen]);

    useEffect(() => {
        if (!isMapReady) {
            return;
        }

        const frameId = requestAnimationFrame(() => {
            window.dispatchEvent(new Event('resize'));
        });

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [isMapReady, zoomClosed, zoomLevel, barrierFreeScale]);

    useInteraction(containerRef, () => {
        // Don't reset countdown if TimeExtensionModal is open
        // Other modals should still allow countdown reset on interaction
        const { isModalInStack } = useModalStackStore.getState();
        const isTimeExtensionModalOpen = isModalInStack('time-extension-modal');

        if (!isTimeExtensionModalOpen) {
            resetCountdown();
        }
    });

    return (
        <section className={styles['horizontal-view']}>
            <section className={styles['horizontal-view__main']}>
                <section
                    className={clsx(styles['horizontal-view__sidebar'], {
                        [styles['horizontal-view__sidebar--show']]: !state.isShowAllFloorGuide,
                        [styles['horizontal-view__sidebar--hide']]: state.isShowAllFloorGuide,
                    })}
                >
                    <HorizontalMapHeader
                        tabIndex={1}
                        selectedFloorName={selectedFloorItem?.name}
                        isShowAllFloorGuide={state.isShowAllFloorGuide}
                        buildingName={sidebarBuildingName}
                        isMultiBuilding={isMultiBuilding}
                    />
                    <HorizontalFloorList
                        floors={sortedFloors}
                        buildingFloorGroups={buildingFloorGroups}
                        selectedFloorName={selectedFloorName}
                        onSelectFloor={async (_floorName: string, floorId: string) => {
                            handleSetModalState({ nearbyPoiList: false });
                            handleResetNearbyState();
                            handleSetState({ isShowAllFloorGuide: false });
                            stopRouteSimulation();
                            clearRouteSimulation();
                            handleSetModalState({ navigationGuide: false });
                            setRoutes(undefined);
                            handleSetState({ navigationStatus: NavigationStatus.DONE });
                            resetFloorGuideType();
                            setSelectedCategoryCode(undefined);
                            if (markerIds?.selectedPoi) {
                                clearMarker(markerIds?.selectedPoi);
                            }
                            if (Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length) {
                                clearMarker(markerIds?.poiList);
                            }
                            setMarkerIds({ selectedPoi: undefined, poiList: undefined });
                            const kioskFloorId = startPoint?.floorId ?? server?.floor?.floorCode;
                            const isKioskFloor = floorId === kioskFloorId;
                            if (isKioskFloor) {
                                await handleChangeFloor(floorId);
                                await addMyCurrentLocation();
                            } else {
                                await handleChangeFloor(floorId, undefined, { moveToCenter: true });
                            }
                        }}
                        isShowAllFloorGuide={state.isShowAllFloorGuide}
                        setIsShowAllFloorGuide={async (isShow: boolean) => {
                            handleSetModalState({ nearbyPoiList: false });
                            handleResetNearbyState();
                            handleSetState({ isShowAllFloorGuide: isShow });
                            handleSetModalState({ navigationGuide: false });
                            await handleStopSimulation();
                        }}
                        onShowMyLocation={async () => await handleShowMyLocation('show-location')}
                        onShowPoiList={() => {
                            handleSetModalState({ poiList: !modalState.poiList });
                            handleSetModalState({ nearbyPoiList: false });
                            handleResetNearbyState();
                            setSelectedCategoryCode(undefined);
                        }}
                    />
                </section>
                <section className={styles['horizontal-view__content']}>
                    <div className={styles['horizontal-view__map-container']}>
                        <div
                            ref={containerRef}
                            className={clsx(
                                {
                                    [styles['horizontal-view__map-container__show']]: !state.isShowAllFloorGuide,
                                    [styles['horizontal-view__map-container__hide']]: state.isShowAllFloorGuide,
                                },
                                'h-full w-full'
                            )}
                            style={mapContainerStyle}
                        >
                            <div
                                className={clsx('h-full w-full', {
                                    [styles['horizontal-view__map-container__magnify-scroll']]: !zoomClosed,
                                })}
                                ref={mapContainerRef}
                                style={mapInnerStyle}
                            />
                        </div>
                    <section
                        className={clsx(styles['horizontal-view__zoom-control'], {
                            hidden: state.isShowAllFloorGuide,
                            block: !state.isShowAllFloorGuide,
                        })}
                    >
                        <ZoomControlBtnGroup
                            tabIndex={5}
                            regionOrder={5}
                            disabledZoomIn={isDisabledZoomIn}
                            disabledZoomOut={isDisabledZoomOut}
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                        />
                    </section>

                    {modalState.navigationGuide && (
                        <div className={clsx(styles['horizontal-view__navigation-guide'], styles['horizontal-view__navigation-guide--fullscreen'])}>
                            <NavigationGuide
                                isActive={modalState.navigationGuide && screenMode === 'fullscreen'}
                                isShowPauseBtn={state.navigationStatus === NavigationStatus.RUNNING || state.navigationStatus === NavigationStatus.IDLE}
                                isShowResumeBtn={state.navigationStatus === NavigationStatus.PAUSED}
                                isShowWatchAgainBtn={state.navigationStatus === NavigationStatus.DONE}
                                directionMaxVisibleRows={3}
                                qrHereUrl={generateQrHereUrl(server?.store?.qrHereId ?? '', startPoint, endPoint?.poiId ?? '')}
                                buildingName={currentBuilding?.name?.[language] ?? ''}
                                directions={generateDisplayDirections(
                                    t,
                                    (floorId: string) => {
                                        const floorName = getFloorById(floorId)?.name?.find((item) => item.lang === language)?.text ?? '';
                                        return t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floorName) });
                                    },
                                    selectedRoute?.navigationList,
                                    poiItemBySelectedEndPointId?.name?.[language] || poiItemBySelectedEndPointId?.name?.['ko'] || '',
                                    (buildingId?: string) => {
                                        if (!Array.isArray(buildings) || !buildings?.length) {
                                            return '';
                                        }
                                        const building = buildings.find((building) => building?.studioBuildingId === buildingId);
                                        return building?.name?.[language] || building?.name?.['ko'] || '';
                                    }
                                )}
                                startFloorName={server?.floor?.name?.[language] || server?.floor?.name?.['ko'] || ''}
                                endPoint={generateNavigationGuideEndPoint}
                                onStop={handleStopSimulation}
                                onResume={handleResumeSimulation}
                                onPause={handlePauseSimulation}
                                onWatchAgain={handleWatchAgain}
                            />
                        </div>
                    )}
                    {modalState.nearbyPoiList && (
                        <div className={styles['horizontal-view__nearby-poi-list-dropdown']}>
                            <NearbyPoiListDropdown
                                type={nearbyState?.type}
                                isActive={modalState.nearbyPoiList}
                                poiList={nearbyState.nearbyPoiList}
                                selectedPoiId={nearbyState.selectedPoiId}
                                title={nearbyState?.title ?? ''}
                                floorName={nearbyState?.floorName ?? ''}
                                buildingName={nearbyState?.building ?? ''}
                                onSelectPoi={(poi) => {
                                    const selectedPoiId = `${poi?.id}${POI_ID_DELIMITER}${poi?.nearbyPoi?.id}`;
                                    handleSetNearbyState({ selectedPoiId: selectedPoiId });
                                }}
                                onStartNavigationGuide={async (poi) => {
                                    handleSetModalState({ nearbyPoiList: false });
                                    const poiListMarkerIds = Array.isArray(markerIds?.poiList) && markerIds?.poiList?.length ? markerIds?.poiList : [];
                                    const clearMarkerIds = [markerIds?.selectedPoi ?? '', ...poiListMarkerIds];
                                    clearMarker(clearMarkerIds);
                                    setMarkerIds({ selectedPoi: undefined, poiList: undefined });
                                    await handleStartNavigationGuide(poi as IPoi);
                                }}
                                onClose={handleCloseNearbyPoiList}
                            />
                        </div>
                    )}
                    <div data-region-nav={!state.isShowAllFloorGuide && !zoomClosed} data-region-order={7}>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['horizontal-view__change-zoom'], styles['horizontal-view__change-zoom--up'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={3}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.up')}
                                icon={<ChevronUpIcon />}
                                onClick={moveUp}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['horizontal-view__change-zoom'], styles['horizontal-view__change-zoom--left'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={3}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.left')}
                                icon={<ChevronLeftIcon />}
                                onClick={moveLeft}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['horizontal-view__change-zoom'], styles['horizontal-view__change-zoom--down'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={5}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.down')}
                                icon={<ChevronDownIcon />}
                                onClick={moveDown}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                        <div
                            tabIndex={-1}
                            className={clsx(styles['horizontal-view__change-zoom'], styles['horizontal-view__change-zoom--right'], {
                                hidden: state.isShowAllFloorGuide || zoomClosed,
                                block: !state.isShowAllFloorGuide && !zoomClosed,
                            })}
                        >
                            <ChangeRegionButton
                                tabIndex={4}
                                data-interactive
                                aria-label={t('floor_guide.text_to_speech.move.right')}
                                icon={<ChevronRightIcon />}
                                onClick={moveRight}
                                data-role={t('common.data_role.button')}
                            />
                        </div>
                    </div>
                </div>
                    {modalState.navigationGuide && (
                        <div className={clsx(styles['horizontal-view__navigation-guide'], styles['horizontal-view__navigation-guide--split'])}>
                            <NavigationGuide
                                isActive={modalState.navigationGuide && screenMode === 'split'}
                                isShowPauseBtn={state.navigationStatus === NavigationStatus.RUNNING || state.navigationStatus === NavigationStatus.IDLE}
                                isShowResumeBtn={state.navigationStatus === NavigationStatus.PAUSED}
                                isShowWatchAgainBtn={state.navigationStatus === NavigationStatus.DONE}
                                directionMaxVisibleRows={3}
                                qrHereUrl={generateQrHereUrl(server?.store?.qrHereId ?? '', startPoint, endPoint?.poiId ?? '')}
                                buildingName={handleGetBuildingNameById(currentMapBuildingId || '') || ''}
                                directions={generateDisplayDirections(
                                    t,
                                    (floorId: string) => {
                                        const floorName = getFloorById(floorId)?.name?.find((n: { lang: string; text: string }) => n.lang === language)?.text || '';
                                        return t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floorName) });
                                    },
                                    selectedRoute?.navigationList,
                                    poiItemBySelectedEndPointId?.name?.[language] || poiItemBySelectedEndPointId?.name?.['ko'] || '',
                                    (buildingId?: string) => {
                                        if (!Array.isArray(buildings) || !buildings?.length) {
                                            return '';
                                        }
                                        const building = buildings.find((building) => building?.studioBuildingId === buildingId);
                                        return building?.name?.[language] || building?.name?.['ko'] || '';
                                    }
                                )}
                                startFloorName={server?.floor?.name?.[language] || server?.floor?.name?.['ko'] || ''}
                                endPoint={generateNavigationGuideEndPoint}
                                onStop={handleStopSimulation}
                                onResume={handleResumeSimulation}
                                onPause={handlePauseSimulation}
                                onWatchAgain={handleWatchAgain}
                            />
                        </div>
                    )}
                </section>
            </section>
            {categoryEnabled && (
                <section
                    className={clsx(styles['horizontal-view__category-list'], {
                        [styles['horizontal-view__category-list--show']]: !state.isShowAllFloorGuide,
                        [styles['horizontal-view__category-list--hide']]: state.isShowAllFloorGuide,
                    })}
                >
                    <HorizontalCategoryList
                        groupedPoiByBuildingId={groupedPoiByBuildingId}
                        onSelectCategory={handleSelectCategory}
                        selectedCategoryCode={selectedCategory?.categoryCode}
                    />
                </section>
            )}

            {modalState.poiList && (
                <PoiListPopup
                    isOpen={modalState.poiList}
                    onClose={() => {
                        handleSetModalState({ poiList: false });
                    }}
                    mapData={mapData}
                    currentFloorName={selectedFloorName}
                    handlePoiClick={(data) => {
                        handlePoiClick(data);
                        handleSetState({ shouldReopenPoiList: true });
                    }}
                />
            )}
            {modalState.noPoiCategory && (
                <NoCategoryPoiPopup
                    isOpen={modalState.noPoiCategory}
                    onClose={async () => {
                        restoreFocus();
                        handleSetModalState({ noPoiCategory: false });
                        setSelectedCategoryCode(undefined);
                    }}
                    category={selectedCategory?.name || selectedCategory?.name}
                    building={handleGetBuildingNameById(state.nearestPoiFloor?.buildingId || currentMapBuildingId || '')}
                    floorName={state.nearestPoiFloor?.name?.[language] || state.nearestPoiFloor?.name?.['ko'] || ''}
                    onGoToFloor={handleGoToFloor}
                />
            )}

            {poiDetailModalState.poiDetail &&
                poiContentSlide &&
                (isMinimalPoiDetail ? (
                    <MinimalPoiModal
                        isOpen={poiDetailModalState.poiDetail}
                        language={language}
                        poiContent={poiContentSlide}
                        showLocationCheck={false}
                        onClose={() => {
                            handleSetPoiDetailModalState({ poiDetail: false });
                            if (state.shouldReopenPoiList) {
                                handleSetState({ shouldReopenPoiList: false });
                                handleSetModalState({ poiList: true });
                            }
                        }}
                        onLocationCheck={handleMinimalPoiLocationCheck}
                        onGuideStart={() => {
                            handleStartGuide();
                            handleSetState({ shouldReopenPoiList: false });
                            handleSetPoiDetailModalState({ poiDetail: false });
                            handleSetModalState({ poiList: false });
                        }}
                    />
                ) : (
                    <FloorGuideHorizontalPoiModal
                        isOpen={poiDetailModalState.poiDetail}
                        language={language}
                        poiContent={poiContentSlide}
                        relatedPoiContents={relatedPoiContentList}
                        selectedPoiContentId={selectedPoiContentId}
                        onSelectPoiContent={(poi) => {
                            setSelectedPoiContent(poi);
                            setSelectedContentId(poi.id);
                            TrackingService.trackPoi(poi.id, undefined, poi);
                        }}
                        onClose={() => {
                            handleSetPoiDetailModalState({ poiDetail: false });
                            if (state.shouldReopenPoiList) {
                                handleSetState({ shouldReopenPoiList: false });
                                handleSetModalState({ poiList: true });
                            }
                        }}
                        onGuideStart={() => {
                            handleStartGuide();
                            handleSetState({ shouldReopenPoiList: false });
                            handleSetPoiDetailModalState({ poiDetail: false });
                            handleSetModalState({ poiList: false });
                        }}
                    />
                ))}

            {modalState.selectTransportationMode && isNonEmptyObject(routes) && (
                <SelectTransportationModeModal
                    isOpen={modalState.selectTransportationMode}
                    onClose={async () => {
                        handleSetModalState({ selectTransportationMode: false });
                        handleSetState({ navigationStatus: NavigationStatus.IDLE });
                        setRoutes(undefined);
                        setSelectedRoute(undefined);
                        resetSelectedPoi();
                        resetFloorGuideType();
                        setSelectedCategoryCode(undefined);
                        await handleShowMyLocation();
                    }}
                    onSelect={handleSelectTransportationMode}
                    routes={isNonEmptyObject(routes) ? (Object.keys(routes) as TRANSPORTATION_REQUEST_TYPE[]) : undefined}
                />
            )}
            {loading && <ContentLoading loading={loading} />}
        </section>
    );
}
